import { TriangleAlertIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';

import { useConfirmStore } from './store';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** `data-testid` of the confirm button. */
  confirmTestId?: string;
  /** May be async: the confirm button shows a spinner and the dialog closes when it resolves. */
  onConfirm: () => void | Promise<void>;
}

/** Controlled confirmation dialog (alertdialog role, focus on Cancel for destructive actions). */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = false,
  confirmTestId,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation('shared');
  const [pending, setPending] = useState(false);

  const handleConfirm = async () => {
    setPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <AlertDialogContent>
        <div className="flex gap-4">
          {destructive && (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-destructive-soft text-destructive-text">
              <TriangleAlertIcon className="size-5" />
            </div>
          )}
          <AlertDialogHeader className={cn(destructive && 'pt-0.5')}>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            {description ? (
              <AlertDialogDescription>{description}</AlertDialogDescription>
            ) : (
              <AlertDialogDescription className="sr-only">{title}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{cancelLabel ?? t('actions.cancel')}</AlertDialogCancel>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            loading={pending}
            onClick={() => void handleConfirm()}
            autoFocus={!destructive}
            data-testid={confirmTestId}
          >
            {confirmLabel ?? (destructive ? t('actions.delete') : t('actions.confirm'))}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Renders dialogs requested through `confirm()`. Mount once near the app root. */
export function ConfirmDialogHost() {
  const current = useConfirmStore((s) => s.current);
  const settle = useConfirmStore((s) => s.settle);
  if (!current) return null;
  return (
    <ConfirmDialog
      key={current.id}
      open
      onOpenChange={(open) => !open && settle(current.id, false)}
      title={current.title}
      description={current.description}
      confirmLabel={current.confirmLabel}
      cancelLabel={current.cancelLabel}
      destructive={current.destructive}
      confirmTestId={current.confirmTestId}
      onConfirm={() => settle(current.id, true)}
    />
  );
}
